import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import { X, Receipt, Save } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", nif: "", address: "", postal_code: "", city: "",
  country: "PT", email: "", wants_invoice_with_nif: true,
};

const BillingModal = ({ open, onClose, onConfirm, user, required = false }) => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAcceptedTerms(false);
    setLoading(true);
    (async () => {
      try {
        const res = await client.get("/billing-profile");
        const bp = res.data.billing_profile;
        if (bp) setForm({ ...empty, ...bp });
        else setForm({ ...empty,
          name: user?.name || "",
          email: user?.email || "",
        });
      } catch {
        setForm({ ...empty, name: user?.name || "", email: user?.email || "" });
      } finally { setLoading(false); }
    })();
  }, [open, user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!acceptedTerms) {
      toast.error("Tens de aceitar os Termos e Condições e a Política de Privacidade.");
      return;
    }
    setSubmitting(true);
    try {
      // Persist server-side first (validates NIF/postal code)
      await client.put("/billing-profile", form);
      onConfirm?.(form);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail || "Dados inválidos.";
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="billing-modal">
      <div className="bg-[#161920] border border-white/10 rounded-2xl max-w-xl w-full p-8 my-8">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#C5A880]/10 border border-[#C5A880]/40 flex items-center justify-center">
              <Receipt className="w-5 h-5 text-[#C5A880]" />
            </div>
            <div>
              <span className="label-uppercase">Dados de faturação</span>
              <h2 className="font-display text-2xl mt-1">Para emitir a tua fatura-recibo</h2>
            </div>
          </div>
          <button onClick={required ? undefined : onClose}
            disabled={required}
            className={`text-[#A29F9B] hover:text-white ${required ? "opacity-30 cursor-not-allowed" : ""}`}
            title={required ? "Preenche os dados de faturação para continuar" : "Fechar"}
            data-testid="billing-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-[#A29F9B] mb-6 leading-relaxed">
          <strong className="text-[#DCD7D2]">Toda a venda gera Fatura+Recibo</strong> emitidos pela Solfut, Lda
          através do sistema certificado Moloni. O documento fica disponível para download em PDF na zona "Faturação"
          e é enviado por email após a confirmação do pagamento.
          {" "}<span className="text-[#A29F9B]/80">O NIF é opcional — sem NIF, a fatura é emitida em nome de "Consumidor Final".</span>
        </p>

        {required && (
          <div className="mb-6 p-3 rounded-xl bg-[#C5A880]/10 border border-[#C5A880]/40 text-sm text-[#DCD7D2]"
               data-testid="billing-required-notice">
            <strong className="text-[#C5A880]">Dados em falta.</strong> Precisamos destes campos para emitir
            a fatura em conformidade — é um passo único, depois ficam guardados.
          </div>
        )}

        {loading ? (
          <div className="text-[#A29F9B]">A carregar...</div>
        ) : (
          <form className="space-y-4" onSubmit={(e)=>{ e.preventDefault(); submit(); }}>
            <div>
              <label className="label-uppercase">Nome completo / Razão social</label>
              <input className="input-elegant mt-2" value={form.name}
                onChange={(e)=>set("name", e.target.value)}
                placeholder="João da Silva ou Solfut, Lda"
                data-testid="billing-name" required />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label-uppercase">NIF (opcional)</label>
                <input className="input-elegant mt-2" value={form.nif}
                  onChange={(e)=>set("nif", e.target.value.replace(/\s/g, ""))}
                  placeholder="123456789"
                  data-testid="billing-nif"
                  inputMode="numeric" maxLength={9} />
                <p className="mt-2 text-xs text-[#A29F9B]">Sem NIF a fatura é emitida em "Consumidor Final".</p>
              </div>
              <div>
                <label className="label-uppercase">País</label>
                <select value={form.country} onChange={(e)=>set("country", e.target.value)}
                  className="input-elegant mt-2"
                  data-testid="billing-country">
                  <option value="PT">Portugal</option>
                  <option value="ES">Espanha</option>
                  <option value="FR">França</option>
                  <option value="GB">Reino Unido</option>
                  <option value="DE">Alemanha</option>
                  <option value="BR">Brasil</option>
                </select>
              </div>
            </div>

            <div>
              <label className="label-uppercase">Morada</label>
              <input className="input-elegant mt-2" value={form.address}
                onChange={(e)=>set("address", e.target.value)}
                placeholder="Rua das Flores 12, 3.º Esq."
                data-testid="billing-address" required />
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="label-uppercase">Código postal</label>
                <input className="input-elegant mt-2" value={form.postal_code}
                  onChange={(e)=>set("postal_code", e.target.value)}
                  placeholder="1000-100"
                  data-testid="billing-postal" required />
              </div>
              <div className="sm:col-span-2">
                <label className="label-uppercase">Localidade</label>
                <input className="input-elegant mt-2" value={form.city}
                  onChange={(e)=>set("city", e.target.value)}
                  placeholder="Lisboa"
                  data-testid="billing-city" required />
              </div>
            </div>

            <div>
              <label className="label-uppercase">Email para a fatura (opcional)</label>
              <input className="input-elegant mt-2" type="email" value={form.email || ""}
                onChange={(e)=>set("email", e.target.value)}
                placeholder={user?.email || "fatura@exemplo.pt"}
                data-testid="billing-email" />
            </div>

            <label className="flex items-start gap-3 mt-6 p-4 rounded-2xl bg-[#0C0E12] border border-white/5 cursor-pointer hover:border-white/15 transition-colors"
              data-testid="billing-accept-wrap">
              <input type="checkbox"
                checked={acceptedTerms}
                onChange={(e)=>setAcceptedTerms(e.target.checked)}
                className="mt-1 accent-[#C5A880]"
                data-testid="billing-accept-terms" />
              <span className="text-xs text-[#DCD7D2] leading-relaxed">
                Li e aceito os <Link to="/termos" target="_blank" className="text-[#C5A880] hover:underline">Termos e Condições</Link>,
                {" "}a <Link to="/privacidade" target="_blank" className="text-[#C5A880] hover:underline">Política de Privacidade</Link>
                {" "}e a <Link to="/reembolsos" target="_blank" className="text-[#C5A880] hover:underline">Política de Reembolso</Link>.
                {" "}<span className="text-[#A29F9B]">Compreendo que ao confirmar o pagamento, este será processado pela EuPago e
                a Solfut, Lda emitirá automaticamente Fatura e Recibo.</span>
              </span>
            </label>

            <div className="flex justify-end gap-3 pt-2">
              {!required && (
                <button type="button" onClick={onClose} className="btn-secondary"
                  data-testid="billing-cancel">Cancelar</button>
              )}
              <button type="submit" disabled={submitting || !acceptedTerms}
                className={`btn-primary inline-flex items-center gap-2 ${(!acceptedTerms || submitting) ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid="billing-confirm">
                <Save className="w-4 h-4" />
                {submitting ? "A guardar..." : "Continuar para pagamento"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BillingModal;
